import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { qk } from "@/api/queryKeys";
import { useApiMutation } from "@/api/useApiQuery";
import { useUserKey } from "@/api/useUserKey";
import { deleteCustomView as deleteCustomViewUrl } from "@/service/reqUrl.service";

import type { DeleteCustomViewParams } from "./types";

interface UseDeleteCustomViewResult {
  /** Remove one saved view via POST /dashboard/delete-custom-view. Resolves to
   *  false when the call fails, so the caller can put the view back. */
  deleteCustomView: (appUserPerfID: number | string) => Promise<boolean>;
}

/**
 * Calls `POST /dashboard/delete-custom-view` (`[DayLight].[DeleteAppUserPerfs]`).
 * Fired from the saved-views picker's ✕. The row is identified by its
 * `AppUserPerfID`; customer/email come from the JWT, so a user can only delete
 * their own views.
 *
 * Invalidation is scoped to the whole `prefs` subtree rather than one dashboard,
 * because the delete call carries only the row id — it has no idea which
 * dashboard the row belonged to. That is deliberately cheap: `invalidateQueries`
 * refetches only entries with ACTIVE observers, and just marks the rest stale.
 * Since one dashboard is on screen at a time, the effect is the same as a scoped
 * invalidation would be.
 */
export function useDeleteCustomView(): UseDeleteCustomViewResult {
  const queryClient = useQueryClient();
  const userKey = useUserKey();

  const { run } = useApiMutation<number | string>({
    url: deleteCustomViewUrl,
    fallbackMessage: "Failed to delete view",
    buildBody: (appUserPerfID): DeleteCustomViewParams => ({ appUserPerfID }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.prefs(userKey) });
    },
  });

  const deleteCustomView = useCallback(
    (appUserPerfID: number | string): Promise<boolean> => run(appUserPerfID),
    [run],
  );

  return { deleteCustomView };
}
